import React from 'react';
import {useDistribution} from '../hooks/useDistribution';
import {PermissionDeniedCard} from '../../../Presentation/components/common';
import Card from '../../../Presentation/components/ui/Card';
import {BarChart3, Users, CheckCircle, Clock} from 'lucide-react';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';

const StatsOverviewTab: React.FC = () => {
  const {hasPermission} = useAuthorization();

  const {
    totalRecords,
    pendingCount,
    completedCount,
    marketers,
    products
  } = useDistribution();

  // Check if user has permission to view distribution stats
  if (!hasPermission(PERMISSIONS.READ_DISTRIBUTION)) {
    return (
      <PermissionDeniedCard
        title="غير مصرح لك"
        message="ليس لديك صلاحية لعرض إحصائيات التوزيع"
      />
    );
  }

  // حساب النسب المئوية
  const completionRate = totalRecords > 0 ? Math.round((completedCount / totalRecords) * 100) : 0;
  const pendingRate = totalRecords > 0 ? Math.round((pendingCount / totalRecords) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-brand-600" />
          نظرة عامة على الإحصائيات
        </h2>
        <p className="text-gray-600 mt-1">ملخص سريع لأداء نظام التوزيع</p>
      </div>

      {/* الإحصائيات الأساسية */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* إجمالي التوزيعات */}
        <Card className="p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <BarChart3 className="h-8 w-8 text-brand-600" />
          </div>
          <h3 className="text-2xl font-bold text-gray-800 mb-1">{totalRecords}</h3>
          <p className="text-gray-600">إجمالي التوزيعات</p>
        </Card>

        {/* التوزيعات المكتملة */}
        <Card className="p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-green-600 mb-1">{completedCount}</h3>
          <p className="text-gray-600">مكتملة ({completionRate}%)</p>
        </Card>

        {/* التوزيعات المعلقة */}
        <Card className="p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <Clock className="h-8 w-8 text-orange-600" />
          </div>
          <h3 className="text-2xl font-bold text-orange-600 mb-1">{pendingCount}</h3>
          <p className="text-gray-600">معلقة ({pendingRate}%)</p>
        </Card>

        {/* عدد المسوقين */}
        <Card className="p-6 text-center">
          <div className="flex items-center justify-center mb-3">
            <Users className="h-8 w-8 text-purple-600" />
          </div>
          <h3 className="text-2xl font-bold text-purple-600 mb-1">{marketers.length}</h3>
          <p className="text-gray-600">مسوق نشط</p>
        </Card>
      </div>

      {/* ملخص سريع */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-800">ملخص سريع</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-brand-600 mb-2">{products.length}</div>
            <p className="text-gray-600">منتج متاح</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600 mb-2">{completionRate}%</div>
            <p className="text-gray-600">معدل الإنجاز</p>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600 mb-2">
              {totalRecords > 0 ? Math.round(totalRecords / 30) : 0}
            </div>
            <p className="text-gray-600">متوسط التوزيعات/يوم</p>
          </div>
        </div>
      </Card>

      {/* رسالة تشجيعية محذوفة بناءً على طلب العميل */}
    </div>
  );
};

export default StatsOverviewTab;
